import React from 'react';
import BulkCheckbox from './BulkCheckbox';

// ---------------------------------------------------------------------------
// Layout partagé par toutes les tables données : sélection (gauche) + actions (droite) fixes, colonnes du milieu dans l'espace restant.
// Les deux colonnes latérales ont la même largeur et leur contenu est centré.
// ---------------------------------------------------------------------------

/** Largeur fixe de la colonne de sélection (plus compacte, effet carré). */
export const SELECTION_COLUMN_WIDTH = 48;
/** Largeur colonne actions — desktop (⋯ + edit + delete). */
export const ACTIONS_COLUMN_WIDTH = 120;
/** Largeur colonne actions — mobile (⋯ only). */
export const ACTIONS_COLUMN_WIDTH_MOBILE = 56;

interface TableProps {
  children: React.ReactNode;
  className?: string;
  /** Désactive le scroll horizontal du wrapper. */
  disableOverflowX?: boolean;
  /**
   * Nombre total de colonnes pour une table "données" : 1 (sélection) + N (colonnes métier) + 1 (actions).
   * Utilise SELECTION_COLUMN_WIDTH à gauche, ACTIONS_COLUMN_WIDTH à droite, le reste au milieu.
   * À utiliser pour LeadTable, PaymentTable, et toute future table avec sélection + actions.
   */
  dataTableColumnCount?: number;
}

const Table: React.FC<TableProps> = ({
  children,
  className = '',
  dataTableColumnCount,
  disableOverflowX = false,
}) => {
  const useDataTableLayout = dataTableColumnCount != null;
  const tableStyle = useDataTableLayout
    ? ({
        ['--table-selection-width' as any]: `${SELECTION_COLUMN_WIDTH}px`,
      } as React.CSSProperties)
    : undefined;

  return (
    <div className={`${disableOverflowX ? 'overflow-x-hidden' : 'overflow-x-auto'} min-w-0`}>
      <table
        className={`w-full divide-y divide-gray-200/40 ${
          useDataTableLayout
            ? `min-w-full table-fixed [--table-actions-width:${ACTIONS_COLUMN_WIDTH_MOBILE}px] sm:[--table-actions-width:${ACTIONS_COLUMN_WIDTH}px]`
            : ''
        } ${className}`}
        style={tableStyle}
      >
        {children}
      </table>
    </div>
  );
};

interface TableHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export const TableHeader: React.FC<TableHeaderProps> = ({ children, className = '' }) => {
  return (
    <thead className={`bg-gray-50/50 backdrop-blur-sm ${className}`}>
      {children}
    </thead>
  );
};

interface TableBodyProps {
  children: React.ReactNode;
  className?: string;
}

export const TableBody: React.FC<TableBodyProps> = ({ children, className = '' }) => {
  return (
    <tbody className={`bg-white divide-y divide-gray-200/40 ${className}`}>
      {children}
    </tbody>
  );
};

/** Full-width empty / loading row inside a data table (header stays mounted). */
export const TableEmptyRow: React.FC<{ colSpan: number; children: React.ReactNode }> = ({
  colSpan,
  children,
}) => (
  <tr>
    <td colSpan={colSpan} className="border-0 p-0">
      {children}
    </td>
  </tr>
);

interface TableRowProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  [key: `data-${string}`]: string | undefined;
}

export const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ children, className = '', onClick, ...dataProps }, ref) => {
    return (
      <tr
        ref={ref}
        className={`transition-colors duration-150 ${
          onClick ? 'cursor-pointer hover:bg-gray-50/80 active:bg-gray-100/50' : ''
        } ${className}`}
        onClick={onClick}
        {...dataProps}
      >
        {children}
      </tr>
    );
  }
);
TableRow.displayName = 'TableRow';

interface TableHeadProps {
  children: React.ReactNode;
  className?: string;
  sortable?: boolean;
  onSort?: () => void;
}

export const TableHead: React.FC<TableHeadProps> = ({ 
  children, 
  className = '', 
  sortable = false, 
  onSort 
}) => {
  return (
    <th 
      className={`px-3 lg:px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider ${
        sortable ? 'cursor-pointer hover:bg-gray-100/60 select-none transition-colors duration-150' : ''
      } ${className}`}
      onClick={sortable ? onSort : undefined}
    >
      {children}
    </th>
  );
};

interface TableCellProps {
  children: React.ReactNode;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLTableDataCellElement>) => void;
}

export const TableCell: React.FC<TableCellProps> = ({ children, className = '', onClick }) => {
  return (
    <td
      className={`px-3 lg:px-4 py-3 text-sm leading-relaxed text-gray-900 ${className}`}
      onClick={onClick}
    >
      {children}
    </td>
  );
};

// ---------------------------------------------------------------------------
// Colonnes fixes partagées : sélection (gauche) et actions (droite).
// La largeur est fixée par le parent (Table) via variables CSS ; les cellules
// appliquent seulement l'alignement + la largeur via ces variables.
// ---------------------------------------------------------------------------

/** Styles fixes des colonnes latérales (basés sur variables CSS du parent). */
const SELECTION_COLUMN_STYLE: React.CSSProperties = {
  width: 'var(--table-selection-width)',
  minWidth: 'var(--table-selection-width)',
  maxWidth: 'var(--table-selection-width)',
};
const ACTIONS_COLUMN_STYLE: React.CSSProperties = {
  width: 'var(--table-actions-width)',
  minWidth: 'var(--table-actions-width)',
  maxWidth: 'var(--table-actions-width)',
};
/** Classe commune : contenu centré. */
const SIDE_COLUMN_CLASS = 'text-center align-middle';

// --- Selection column ---
const SELECTION_COLUMN_CLASS = `hidden sm:table-cell cursor-pointer p-0 ${SIDE_COLUMN_CLASS}`;
// --- Actions column ---
const ACTIONS_COLUMN_CLASS = `px-1 sm:px-3 ${SIDE_COLUMN_CLASS}`;
const ACTIONS_HEADER_CLASS = `px-1 sm:px-3 ${SIDE_COLUMN_CLASS}`;

export interface TableSelectionHeaderProps {
  isAllSelected: boolean;
  selectedCount: number;
  onSelectAll: () => void;
  selectAllAriaLabel: string;
  hidden?: boolean;
}

export const TableSelectionHeader: React.FC<TableSelectionHeaderProps> = ({
  isAllSelected,
  selectedCount,
  onSelectAll,
  selectAllAriaLabel,
  hidden = false,
}) => {
  if (hidden) {
    return (
      <th
        className={SELECTION_COLUMN_CLASS}
        style={SELECTION_COLUMN_STYLE}
        aria-hidden="true"
      />
    );
  }
  return (
    <th className={SELECTION_COLUMN_CLASS} style={SELECTION_COLUMN_STYLE}>
      <div
        role="button"
        tabIndex={0}
        className="w-full py-3 flex items-center justify-center cursor-pointer select-none"
        onClick={onSelectAll}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelectAll();
          }
        }}
        aria-label={selectAllAriaLabel}
      >
        <BulkCheckbox
          isSelected={isAllSelected}
          isIndeterminate={selectedCount > 0 && !isAllSelected}
          onToggle={onSelectAll}
        />
      </div>
    </th>
  );
};

export interface TableSelectionCellProps {
  itemId: string;
  isSelected: boolean;
  onToggle: (id: string) => void;
  hidden?: boolean;
}

export const TableSelectionCell: React.FC<TableSelectionCellProps> = ({
  itemId,
  isSelected,
  onToggle,
  hidden = false,
}) => {
  if (hidden) {
    return (
      <td
        className={SELECTION_COLUMN_CLASS}
        style={SELECTION_COLUMN_STYLE}
        aria-hidden="true"
      />
    );
  }
  return (
    <td
      className={SELECTION_COLUMN_CLASS}
      style={SELECTION_COLUMN_STYLE}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        onToggle(itemId);
      }}
    >
      <span className="inline-flex items-center justify-center pointer-events-none text-gray-400">
        <BulkCheckbox isSelected={isSelected} onToggle={() => onToggle(itemId)} />
      </span>
    </td>
  );
};

// --- Actions column (droite) : même largeur que sélection, contenu centré ---

export interface TableActionsHeaderProps {
  /** Libellé de la colonne (ex. "Actions" ou t('common.actions')). */
  label: string;
}

export const TableActionsHeader: React.FC<TableActionsHeaderProps> = ({ label }) => {
  return (
    <th className={`text-center whitespace-nowrap ${ACTIONS_HEADER_CLASS}`} style={ACTIONS_COLUMN_STYLE}>
      <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">{label}</span>
    </th>
  );
};

export interface TableActionsCellProps {
  children: React.ReactNode;
}

export const TableActionsCell: React.FC<TableActionsCellProps> = ({ children }) => {
  return (
    <td
      className={ACTIONS_COLUMN_CLASS}
      style={ACTIONS_COLUMN_STYLE}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <div className="flex items-center justify-center gap-1 w-full">
        {children}
      </div>
    </td>
  );
};

/** Row className for selected state: light blue background, blue hover (no gray). */
export const tableRowSelectedClass = 'bg-primary-50/50 hover:!bg-primary-50/70';

// FixedThreeColumnTable removed (cleaned up after test layout)

export default Table;
